import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { getCategories } from '@/lib/api';
import SectionHeader from '@/components/SectionHeader';
import type { Category } from '@/types';

const fallbackCategories: Category[] = [
  {
    id: 'cat-metabolic',
    slug: 'metabolic',
    familyNumber: 'Family 01',
    title: 'Metabolic',
    description: 'Weight regulation and GLP-pathway compounds for body-composition protocols.',
  },
  {
    id: 'cat-tissue-repair',
    slug: 'tissue-repair',
    familyNumber: 'Family 02',
    title: 'Tissue Repair',
    description: 'Healing and regenerative peptides, paired stacks for joint, tendon and gut recovery.',
  },
  {
    id: 'cat-gh-axis',
    slug: 'gh-axis',
    familyNumber: 'Family 03',
    title: 'GH Axis',
    description: 'Growth-hormone secretagogues and pulse stacks for recovery and lean composition.',
  },
];

function MoleculeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 48 48"
      fill="none"
      className={className}
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="24" cy="13" r="5" stroke="currentColor" strokeWidth="2" />
      <circle cx="13" cy="31" r="5" stroke="currentColor" strokeWidth="2" />
      <circle cx="35" cy="31" r="5" stroke="currentColor" strokeWidth="2" />
      <path
        d="M21.5 17.5L15.5 27M26.5 17.5L32.5 27M17.5 31H30.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

const MotionLink = motion(Link);

export default function CategoriesSection() {
  const { data: apiCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  const allCategories = apiCategories?.length ? apiCategories : fallbackCategories;
  const categories = allCategories.filter((c) => c.slug !== 'bundles');

  return (
    <section className="border-t border-rp-line bg-rp-paper py-10 md:py-20">
      <div className="rp-container">
        <SectionHeader
          eyebrow="Browse by mechanism"
          title="Pick the pathway"
          description="Looking to recover faster, lean out, or stay sharp longer? Each family below points to the peptides for it."
          align="left"
        />

        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 lg:gap-6">
          {categories.map((category, index) => (
            <MotionLink
              key={category.id}
              to={`/products?category=${category.slug}`}
              initial={false}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -4 }}
              className="group flex h-full flex-col rounded-2xl border border-rp-line bg-rp-bone p-5 transition-shadow duration-200 hover:border-rp-forest/20 hover:shadow-card-hover"
            >
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-full border border-rp-line-2 bg-rp-paper text-rp-forest">
                  <MoleculeIcon className="h-6 w-6" />
                </div>
                <span className="rp-mono-mini text-rp-moss">{category.familyNumber}</span>
              </div>

              <div className="mt-5 flex flex-1 flex-col">
                <h3 className="text-lg font-semibold leading-tight text-rp-forest">
                  {category.title}
                </h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-rp-moss">
                  {category.description}
                </p>
                <div className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-rp-forest">
                  Browse
                  <ArrowRight
                    size={12}
                    className="transition-transform duration-200 group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </div>
              </div>
            </MotionLink>
          ))}
        </div>
      </div>
    </section>
  );
}
